<template>
  <div class="followupbot-analytics">
    <v-container fluid class="px-6 py-4">
      <!-- Header Section -->
      <div class="header-section mb-6">
        <div class="header-content">
          <h1 class="page-title">
            <v-icon large class="mr-3" color="#667eea">mdi-chart-bar</v-icon>
            FollowUpBot Analytics
          </h1>
          <div class="date-range-picker">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  class="date-btn"
                >
                  <v-icon left>mdi-calendar</v-icon>
                  {{ formatDateRange() }}
                </v-btn>
              </template>
              <v-date-picker
                v-model="dateRange"
                range
                @input="dateMenu = false; fetchAnalytics()"
              ></v-date-picker>
            </v-menu>
          </div>
        </div>
      </div>

      <!-- Performance Overview -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper blue--bg">
                <v-icon size="28" color="white">mdi-email-send</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ analytics.total_messages_sent || 0 }}</div>
                <div class="stats-label">Messages Sent</div>
                <div class="stats-change" :class="getChangeClass(analytics.messages_sent_change)">
                  <v-icon small>{{ getChangeIcon(analytics.messages_sent_change) }}</v-icon>
                  {{ Math.abs(analytics.messages_sent_change || 0) }}% vs last period
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper green--bg">
                <v-icon size="28" color="white">mdi-email-open</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ (analytics.avg_open_rate || 0).toFixed(1) }}%</div>
                <div class="stats-label">Open Rate</div>
                <div class="stats-change" :class="getChangeClass(analytics.open_rate_change)">
                  <v-icon small>{{ getChangeIcon(analytics.open_rate_change) }}</v-icon>
                  {{ Math.abs(analytics.open_rate_change || 0) }}% vs last period
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper orange--bg">
                <v-icon size="28" color="white">mdi-reply</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ (analytics.avg_response_rate || 0).toFixed(1) }}%</div>
                <div class="stats-label">Response Rate</div>
                <div class="stats-change" :class="getChangeClass(analytics.response_rate_change)">
                  <v-icon small>{{ getChangeIcon(analytics.response_rate_change) }}</v-icon>
                  {{ Math.abs(analytics.response_rate_change || 0) }}% vs last period
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper purple--bg">
                <v-icon size="28" color="white">mdi-chart-line</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ (analytics.avg_engagement_score || 0).toFixed(1) }}</div>
                <div class="stats-label">Avg Engagement Score</div>
                <div class="stats-change" :class="getChangeClass(analytics.engagement_score_change)">
                  <v-icon small>{{ getChangeIcon(analytics.engagement_score_change) }}</v-icon>
                  {{ Math.abs(analytics.engagement_score_change || 0) }}% vs last period
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts Row -->
      <v-row class="mb-6">
        <!-- Performance Trend Chart -->
        <v-col cols="12" md="8">
          <v-card class="chart-card" elevation="4">
            <v-card-title class="chart-title">
              <h3>Performance Trends</h3>
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="chartPeriod" @change="fetchAnalytics">
                <v-btn small value="7">7 Days</v-btn>
                <v-btn small value="30">30 Days</v-btn>
                <v-btn small value="90">90 Days</v-btn>
              </v-btn-toggle>
            </v-card-title>
            <v-card-text class="chart-container">
              <div class="chart-placeholder">
                <v-icon size="64" color="#e0e0e0">mdi-chart-line</v-icon>
                <div class="placeholder-text">Performance trends chart would be displayed here</div>
                <div class="chart-summary">
                  <div class="summary-item">
                    <strong>Best performing day:</strong> {{ analytics.best_performing_day || 'Tuesday' }}
                  </div>
                  <div class="summary-item">
                    <strong>Peak sending time:</strong> {{ analytics.peak_sending_time || '10:00 AM' }}
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Message Tone Performance -->
        <v-col cols="12" md="4">
          <v-card class="chart-card" elevation="4">
            <v-card-title class="chart-title">
              <h3>Tone Performance</h3>
            </v-card-title>
            <v-card-text>
              <div class="tone-stats">
                <div 
                  v-for="tone in tonePerformance" 
                  :key="tone.tone"
                  class="tone-stat-item"
                >
                  <div class="tone-header">
                    <span class="tone-name">{{ getToneName(tone.tone) }}</span>
                    <span class="tone-rate">{{ tone.response_rate.toFixed(1) }}%</span>
                  </div>
                  <v-progress-linear
                    :value="tone.response_rate"
                    :color="getToneColor(tone.tone)"
                    height="8"
                    rounded
                    class="tone-progress"
                  ></v-progress-linear>
                  <div class="tone-details">
                    {{ tone.total_sent }} sent, {{ tone.total_replies }} replies
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Detailed Analytics -->
      <v-row>
        <!-- Campaign Performance -->
        <v-col cols="12" md="6">
          <v-card class="analytics-card" elevation="4">
            <v-card-title class="card-title">
              <h3>Top Performing Campaigns</h3>
            </v-card-title>
            <v-card-text>
              <div class="campaign-list">
                <div 
                  v-for="campaign in topCampaigns" 
                  :key="campaign.id"
                  class="campaign-item"
                >
                  <div class="campaign-info">
                    <div class="campaign-name">{{ campaign.campaign_name }}</div>
                    <div class="campaign-contact">{{ campaign.contact_name }}</div>
                  </div>
                  <div class="campaign-stats">
                    <div class="stat">
                      <span class="stat-value">{{ campaign.response_rate.toFixed(1) }}%</span>
                      <span class="stat-label">Response Rate</span>
                    </div>
                    <div class="stat">
                      <span class="stat-value">{{ campaign.total_follow_ups_sent }}</span>
                      <span class="stat-label">Messages</span>
                    </div>
                  </div>
                </div>
                <div v-if="topCampaigns.length === 0" class="no-data">
                  No campaign data available
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Recent Activity -->
        <v-col cols="12" md="6">
          <v-card class="analytics-card" elevation="4">
            <v-card-title class="card-title">
              <h3>Recent Activity</h3>
            </v-card-title>
            <v-card-text>
              <div class="activity-list">
                <div 
                  v-for="activity in recentActivity" 
                  :key="activity.id"
                  class="activity-item"
                >
                  <div class="activity-icon">
                    <v-icon :color="getActivityColor(activity.type)" small>
                      {{ getActivityIcon(activity.type) }}
                    </v-icon>
                  </div>
                  <div class="activity-content">
                    <div class="activity-text">{{ activity.description }}</div>
                    <div class="activity-time">{{ formatRelativeTime(activity.created_at) }}</div>
                  </div>
                </div>
                <div v-if="recentActivity.length === 0" class="no-data">
                  No recent activity
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Insights & Recommendations -->
      <v-row class="mt-6">
        <v-col cols="12">
          <v-card class="insights-card" elevation="4">
            <v-card-title class="card-title">
              <v-icon left color="orange">mdi-lightbulb</v-icon>
              <h3>AI Insights & Recommendations</h3>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <div class="insight-item">
                    <div class="insight-title">
                      <v-icon color="success" small class="mr-2">mdi-trending-up</v-icon>
                      Best Performing Time
                    </div>
                    <div class="insight-content">
                      Messages sent on {{ analytics.best_performing_day || 'Tuesday' }} at 
                      {{ analytics.peak_sending_time || '10:00 AM' }} have a 
                      {{ ((analytics.peak_time_response_rate || 35) - (analytics.avg_response_rate || 25)).toFixed(1) }}% 
                      higher response rate.
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="insight-item">
                    <div class="insight-title">
                      <v-icon color="info" small class="mr-2">mdi-message-text</v-icon>
                      Optimal Message Tone
                    </div>
                    <div class="insight-content">
                      {{ getBestTone() }} tone messages perform best for your audience, 
                      with a {{ getBestToneRate() }}% response rate. Consider using this tone more frequently.
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="insight-item">
                    <div class="insight-title">
                      <v-icon color="warning" small class="mr-2">mdi-clock-alert</v-icon>
                      Follow-up Frequency
                    </div>
                    <div class="insight-content">
                      Campaigns with {{ analytics.optimal_frequency || 3 }}-day intervals show 
                      {{ ((analytics.optimal_frequency_rate || 40) - (analytics.avg_response_rate || 25)).toFixed(1) }}% 
                      better engagement. Consider adjusting your frequency.
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "FollowUpBotAIAnalytics",
  data() {
    return {
      isLoading: false,
      dateMenu: false,
      dateRange: [],
      chartPeriod: '30',
      analytics: {
        total_messages_sent: 0,
        avg_open_rate: 0,
        avg_response_rate: 0,
        avg_engagement_score: 0,
        messages_sent_change: 0,
        open_rate_change: 0,
        response_rate_change: 0,
        engagement_score_change: 0,
        best_performing_day: 'Tuesday',
        peak_sending_time: '10:00 AM',
        peak_time_response_rate: 35,
        optimal_frequency: 3,
        optimal_frequency_rate: 40
      },
      tonePerformance: [
        { tone: 'friendly', response_rate: 28.5, total_sent: 120, total_replies: 34 },
        { tone: 'consultative', response_rate: 32.1, total_sent: 98, total_replies: 31 },
        { tone: 'urgent', response_rate: 25.8, total_sent: 85, total_replies: 22 },
        { tone: 'formal', response_rate: 22.4, total_sent: 67, total_replies: 15 }
      ],
      topCampaigns: [],
      recentActivity: []
    };
  },
  async mounted() {
    this.initializeDateRange();
    await this.fetchAnalytics();
  },
  methods: {
    initializeDateRange() {
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
      this.dateRange = [
        thirtyDaysAgo.toISOString().substr(0, 10),
        today.toISOString().substr(0, 10)
      ];
    },
    async fetchAnalytics() {
      try {
        this.isLoading = true;
        const params = {
          start_date: this.dateRange[0],
          end_date: this.dateRange[1],
          period: this.chartPeriod
        };
        
        const response = await axios.get('follow-up-messages/analytics', { params });
        this.analytics = { ...this.analytics, ...response.data.data.overview };
        this.tonePerformance = response.data.data.tone_performance || this.tonePerformance;
        this.topCampaigns = response.data.data.top_campaigns || [];
        this.recentActivity = response.data.data.recent_activity || [];
      } catch (error) {
        console.error('Error fetching analytics:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load analytics. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },
    formatDateRange() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return 'Select Date Range';
      }
      const startDate = new Date(this.dateRange[0]).toLocaleDateString();
      const endDate = new Date(this.dateRange[1]).toLocaleDateString();
      return `${startDate} - ${endDate}`;
    },
    getChangeClass(change) {
      if (change > 0) return 'positive-change';
      if (change < 0) return 'negative-change';
      return 'neutral-change';
    },
    getChangeIcon(change) {
      if (change > 0) return 'mdi-trending-up';
      if (change < 0) return 'mdi-trending-down';
      return 'mdi-trending-neutral';
    },
    getToneName(tone) {
      const names = {
        friendly: 'Friendly',
        consultative: 'Consultative',
        urgent: 'Urgent',
        formal: 'Formal'
      };
      return names[tone] || tone;
    },
    getToneColor(tone) {
      const colors = {
        friendly: 'blue',
        consultative: 'green',
        urgent: 'orange',
        formal: 'purple'
      };
      return colors[tone] || 'grey';
    },
    getBestTone() {
      if (this.tonePerformance.length === 0) return 'Consultative';
      const best = this.tonePerformance.reduce((prev, current) => 
        prev.response_rate > current.response_rate ? prev : current
      );
      return this.getToneName(best.tone);
    },
    getBestToneRate() {
      if (this.tonePerformance.length === 0) return '32.1';
      const best = this.tonePerformance.reduce((prev, current) => 
        prev.response_rate > current.response_rate ? prev : current
      );
      return best.response_rate.toFixed(1);
    },
    getActivityIcon(type) {
      const icons = {
        message_sent: 'mdi-email-send',
        message_opened: 'mdi-email-open',
        message_replied: 'mdi-reply',
        campaign_created: 'mdi-bullhorn',
        message_scheduled: 'mdi-calendar-clock'
      };
      return icons[type] || 'mdi-information';
    },
    getActivityColor(type) {
      const colors = {
        message_sent: 'blue',
        message_opened: 'green',
        message_replied: 'success',
        campaign_created: 'purple',
        message_scheduled: 'orange'
      };
      return colors[type] || 'grey';
    },
    formatRelativeTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return '1 day ago';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
      return `${Math.ceil(diffDays / 30)} months ago`;
    }
  }
};
</script>

<style scoped>
.followupbot-analytics {
  min-height: calc(100vh - 64px);
}

.header-section {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.date-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

/* Stats Cards */
.stats-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
}

.stats-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(102, 126, 234, 0.15) !important;
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px !important;
}

.stats-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.orange--bg { background: linear-gradient(135deg, #ff9500 0%, #ff6b00 100%); }
.green--bg { background: linear-gradient(135deg, #51cf66 0%, #40c057 100%); }
.blue--bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.purple--bg { background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%); }

.stats-text {
  flex: 1;
}

.stats-value {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  margin-bottom: 6px;
}

.stats-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.stats-change {
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 2px;
}

.positive-change { color: #51cf66; }
.negative-change { color: #ff6b6b; }
.neutral-change { color: #64748b; }

/* Chart Cards */
.chart-card, .analytics-card, .insights-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  height: 100%;
}

.chart-title, .card-title {
  padding: 24px 24px 0 24px !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chart-title h3, .card-title h3 {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.chart-container {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px !important;
}

.chart-placeholder {
  text-align: center;
  color: #64748b;
}

.placeholder-text {
  margin: 16px 0;
  font-size: 16px;
}

.chart-summary {
  margin-top: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: left;
}

.summary-item {
  margin-bottom: 8px;
  font-size: 14px;
  color: #2c3e50;
}

/* Tone Performance */
.tone-stats {
  padding: 16px 0;
}

.tone-stat-item {
  margin-bottom: 24px;
}

.tone-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.tone-name {
  font-weight: 600;
  color: #2c3e50;
}

.tone-rate {
  font-weight: 700;
  color: #667eea;
}

.tone-progress {
  margin-bottom: 4px;
}

.tone-details {
  font-size: 12px;
  color: #64748b;
}

/* Campaign List */
.campaign-list, .activity-list {
  max-height: 300px;
  overflow-y: auto;
}

.campaign-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #e9ecef;
}

.campaign-item:last-child {
  border-bottom: none;
}

.campaign-info {
  flex: 1;
}

.campaign-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.campaign-contact {
  font-size: 12px;
  color: #64748b;
}

.campaign-stats {
  display: flex;
  gap: 20px;
}

.stat {
  text-align: center;
}

.stat-value {
  display: block;
  font-weight: 700;
  color: #667eea;
  font-size: 16px;
}

.stat-label {
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
}

/* Activity List */
.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.activity-content {
  flex: 1;
}

.activity-text {
  font-size: 14px;
  color: #2c3e50;
  margin-bottom: 4px;
}

.activity-time {
  font-size: 12px;
  color: #64748b;
}

.no-data {
  text-align: center;
  color: #64748b;
  font-style: italic;
  padding: 40px 0;
}

/* Insights */
.insights-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.insight-item {
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;
}

.insight-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.insight-content {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .page-title {
    font-size: 28px;
    justify-content: center;
  }
  
  .campaign-stats {
    flex-direction: column;
    gap: 8px;
  }
}

@media (max-width: 600px) {
  .header-section {
    padding: 20px;
  }
  
  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .stats-content {
    padding: 16px !important;
    gap: 16px;
  }
  
  .stats-icon-wrapper {
    width: 48px;
    height: 48px;
  }
  
  .stats-value {
    font-size: 24px;
  }
  
  .campaign-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .campaign-stats {
    flex-direction: row;
    gap: 20px;
  }
}
</style>